import { server } from "../../server";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import ExportExcel from "../../components/ExportExcel";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { capitalizeWords, formatDate } from "../../functions";
import { fetchAppliedLeaves } from "../../redux/appliedLeaveSlice";
import { useDispatch, useSelector } from "react-redux";

function AppliedLeave() {
  const dispatch = useDispatch();
  const { data: appliedLeave, loading: fetchLoading } = useSelector(
    (state) => state.appliedLeave
  );

  useEffect(() => {
    dispatch(fetchAppliedLeaves());
  }, [dispatch]);

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];

  const updateLeaveStatus = async (leaveId, status) => {
    Swal.fire({
      title: `Are you sure?`,
      text: `You are about to ${status} this leave request.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: status === "approved" ? "#28a745" : "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${status} it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${server}/update_applied_leave_mongodb`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ leaveId, status }),
            }
          );

          const data = await response.json();
          toast.success(`Leave ${status} successfully`);
          fetchAppliedLeaves();
        } catch (error) {
          console.error("Error updating leave status:", error);
          toast.error("Please Try Again");
        }
      }
    });
  };

  // Filter
  const filteredRecords = appliedLeave.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const columnMapping = {
    leaveId: "Leave ID",
    admission_no: "Admission No",
    name: "Student Name",
    class: "Class",
    section: "Section",
    status: "Status",
    fromDate: "Leave From",
    toDate: "Leave To",
    reason: "Reason",
    document: "Document",
    createdAt: "Applied Date",
  };

  const handleExportExcel = () => {
    ExportExcel(appliedLeave, "ScholarshipDetails", columnMapping);
  };

  const handleExportAppliedLeaveExcel = () => {
    if (!appliedLeave || appliedLeave.length === 0) {
      toast.error("No leave data to export");
      return;
    }

    const sheetData = appliedLeave.map((student) => ({
      "Leave ID": student.leaveId || "",
      "Admission No": student.admission_no || "",
      "Student Name": student.name || "",
      Class: student.class || "",
      Section: student.section || "",
      "Leave From": student.fromDate || "",
      "Leave To": student.toDate || "",
      Reason: student.reason || "",
      Status: capitalizeWords(student.status || ""),
      "Applied Date": formatDate(student.createdAt) || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applied Leave");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AppliedLeave_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAppliedLeaveClassWiseExcel = () => {
    if (!appliedLeave || appliedLeave.length === 0) {
      toast.error("No leave data to export");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const classGroups = {};

    appliedLeave.forEach((student) => {
      const cls = student.class || "Unknown";
      const sec = student.section || "Unknown";

      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];

      classGroups[cls][sec].push(student);
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];

      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const students = sections[sec];

        students.sort((a, b) =>
          (a.admission_no || "").localeCompare(b.admission_no || "")
        );

        combinedData.push({ Section: `Section ${sec}` });
        students.forEach((student) => {
          combinedData.push({
            "Leave ID": student.leaveId || "",
            "Admission No": student.admission_no || "",
            "Student Name": student.name || "",
            "Leave From": student.fromDate || "",
            "Leave To": student.toDate || "",
            Reason: student.reason || "",
            Status: capitalizeWords(student.status || ""),
            "Applied Date": formatDate(student.createdAt) || "",
          });
        });

        combinedData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(combinedData, {
        skipHeader: false,
      });

      const safeSheetName = className.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AppliedLeave_ClassWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAppliedLeavePendingExcel = () => {
    if (!appliedLeave || appliedLeave.length === 0) {
      toast.error("No leave data to export");
      return;
    }

    const approvedLeaves = appliedLeave.filter(
      (student) => (student.status || "").toLowerCase() === "pending"
    );

    if (approvedLeaves.length === 0) {
      toast.warning("No pending leave records found");
      return;
    }

    const sheetData = approvedLeaves.map((student) => ({
      "Leave ID": student.leaveId || "",
      "Admission No": student.admission_no || "",
      "Student Name": student.name || "",
      Class: student.class || "",
      Section: student.section || "",
      "Leave From": student.fromDate || "",
      "Leave To": student.toDate || "",
      Reason: student.reason || "",
      Status: capitalizeWords(student.status || ""),
      "Applied Date": formatDate(student.createdAt) || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applied Leave");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AppliedLeave_Pending_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAppliedLeaveApprovedExcel = () => {
    if (!appliedLeave || appliedLeave.length === 0) {
      toast.error("No leave data to export");
      return;
    }

    const approvedLeaves = appliedLeave.filter(
      (student) => (student.status || "").toLowerCase() === "approved"
    );

    if (approvedLeaves.length === 0) {
      toast.warning("No approved leave records found");
      return;
    }

    const sheetData = approvedLeaves.map((student) => ({
      "Leave ID": student.leaveId || "",
      "Admission No": student.admission_no || "",
      "Student Name": student.name || "",
      Class: student.class || "",
      Section: student.section || "",
      "Leave From": student.fromDate || "",
      "Leave To": student.toDate || "",
      Reason: student.reason || "",
      Status: capitalizeWords(student.status || ""),
      "Applied Date": formatDate(student.createdAt) || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applied Leave");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AppliedLeave_Approved_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAppliedLeaveRejectedExcel = () => {
    if (!appliedLeave || appliedLeave.length === 0) {
      toast.error("No leave data to export");
      return;
    }

    const approvedLeaves = appliedLeave.filter(
      (student) => (student.status || "").toLowerCase() === "rejected"
    );

    if (approvedLeaves.length === 0) {
      toast.warning("No rejected leave records found");
      return;
    }

    const sheetData = approvedLeaves.map((student) => ({
      "Leave ID": student.leaveId || "",
      "Admission No": student.admission_no || "",
      "Student Name": student.name || "",
      Class: student.class || "",
      Section: student.section || "",
      "Leave From": student.fromDate || "",
      "Leave To": student.toDate || "",
      Reason: student.reason || "",
      Status: capitalizeWords(student.status || ""),
      "Applied Date": formatDate(student.createdAt) || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applied Leave");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AppliedLeave_Rejected_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAppliedLeaveDocumentExcel = () => {
    if (!appliedLeave || appliedLeave.length === 0) {
      toast.error("No leave data to export");
      return;
    }

    const withDocument = appliedLeave.filter(
      (student) => student.document && student.document.trim() !== ""
    );

    if (withDocument.length === 0) {
      toast.warning("No leave records with documents found");
      return;
    }

    const sheetData = withDocument.map((student) => ({
      "Leave ID": student.leaveId || "",
      "Admission No": student.admission_no || "",
      "Student Name": student.name || "",
      Class: student.class || "",
      Section: student.section || "",
      "Leave From": student.fromDate || "",
      "Leave To": student.toDate || "",
      Reason: student.reason || "",
      Document: student.document || "",
      Status: capitalizeWords(student.status || ""),
      "Applied Date": formatDate(student.createdAt) || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applied Leave");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AppliedLeave_WithDocument_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((student, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Leave ID</span> &mdash;{" "}
                              {student.leaveId} <br />
                              <span class="text-dark">Admission No</span>{" "}
                              &mdash; {student.admission_no} <br />
                              <span class="text-dark">Student Name</span>{" "}
                              &mdash; {student.name} <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {student.class} <br />
                              <span class="text-dark">
                                Section
                              </span> &mdash; {student.section}
                              <hr className="w-25" />
                              <span class="text-dark">
                                Leave From
                              </span> &mdash; {student.fromDate} <br />
                              <span class="text-dark">
                                Leave To
                              </span> &mdash; {student.toDate} <br />
                              <span class="text-dark">Reason</span> &mdash;{" "}
                              {student.reason
                                .split(" ")
                                .reduce(
                                  (acc, word) => {
                                    const lastLine = acc[acc.length - 1];
                                    const currentLineLength =
                                      lastLine.join(" ").length;
                                    if (
                                      currentLineLength + word.length + 1 >
                                      50
                                    ) {
                                      acc.push([word]);
                                    } else {
                                      lastLine.push(word);
                                    }

                                    return acc;
                                  },
                                  [[]]
                                )
                                .map((group, i) => (
                                  <span key={i}>
                                    {group.join(" ")}
                                    <br />
                                  </span>
                                ))}
                              <br />
                              {student.document && (
                                <>
                                  <span class="text-dark">Document</span>{" "}
                                  &mdash;{" "}
                                  <a
                                    href={student.document}
                                    target="_blank"
                                    className="text-danger"
                                  >
                                    Click Here
                                  </a>{" "}
                                  <br />
                                </>
                              )}
                              <span class="text-dark">Date</span> &mdash;{" "}
                              {formatDate(student.createdAt)}
                            </td>
                            <td>
                              {student.status.toLowerCase() === "pending" ? (
                                <div className="d-flex flex-column gap-1 width-fit-content">
                                  <span
                                    className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                    onClick={() =>
                                      updateLeaveStatus(
                                        student.leaveId,
                                        "approved"
                                      )
                                    }
                                  >
                                    Approve
                                  </span>
                                  <span
                                    className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                    onClick={() =>
                                      updateLeaveStatus(
                                        student.leaveId,
                                        "rejected"
                                      )
                                    }
                                  >
                                    Decline
                                  </span>
                                </div>
                              ) : (
                                <span
                                  className={`badge px-4 rounded-3 ${
                                    student.status === "approved"
                                      ? "bg-success-soft text-success"
                                      : "bg-danger-soft text-danger"
                                  }`}
                                >
                                  {capitalizeWords(student.status)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Attendance</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Applied Leave
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {appliedLeave.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {fetchLoading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Applied Leave
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.4 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAppliedLeaveExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Leave Report
                                </span>
                                <span
                                  onClick={
                                    handleExportAppliedLeaveClassWiseExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class Wise
                                </span>
                                <span
                                  onClick={handleExportAppliedLeavePendingExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Pending
                                </span>
                                <span
                                  onClick={
                                    handleExportAppliedLeaveApprovedExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Approved
                                </span>
                                <span
                                  onClick={
                                    handleExportAppliedLeaveRejectedExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Rejected
                                </span>
                                <span
                                  onClick={
                                    handleExportAppliedLeaveDocumentExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Having Document
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default AppliedLeave;
